import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ButtonCircular } from '@deliveryhero/pd-cookbook/components/ButtonCircular';
import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Tab, Tabs } from '@mui/material';

import { LoadingAnimation } from 'components/LoadingAnimation';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import { IcSubscribe } from 'components/svg/ic-subscribe/ic-subscribe';
import { format } from 'date-fns';
import {
  BulkSubscribePayload,
  BulkSubscribeResponse,
  BulkUnsubscribePayload,
  BulkUnsubscribeResponse,
  Department,
  Subscription,
  SubscriptionTier,
  User
} from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import CallToActions from 'src/components//CallToActions';
// import { EmptyData } from 'src/components/svg/EmptyData';
import AllSubscribed from 'src/components/svg/AllSubscribed';
import { IcUnsubscribe } from 'src/components/svg/ic-unsubscribe';
import ReferAFriend from 'src/components/svg/ReferAFriend'; // todo: replace this with EmptyData after Employee listing PR is merged
import TableList, { RowsPerPage } from 'src/components/TableList';
import { useConfiguration } from 'src/contexts/country-config';
import useAction from 'src/hooks/useAction';
import useApi from 'src/hooks/useApi';
import useNotification from 'src/hooks/useNotification';
import useQueryParam from 'src/hooks/useQueryParam/useQueryParam';
import SubscriptionQueryKeys from 'src/query-keys/subscription';
import { mapSubscriptionDurationToMonth } from 'src/utils/format-user-subscription-details';
import generateQuery from 'src/utils/generate-query';
import isFoodora from 'src/utils/is-foodora/is-foodora';

import { LabelText, TestId } from './constants';
import { LandingPage } from './LandingPage';
import { SubscribePage } from './SubscribePage';
import {
  SubscribedColumns,
  UnsubscribedColumns
} from './SubscriptionListingColumns';
import {
  SubscribedListingFilter,
  UnsubscribedListingFilter
} from './SubscriptionListingFilter/SubscriptionListingFilter';
import {
  SelectionType,
  SubscribedListingData,
  SubscriptionActions,
  SubscriptionListingViews,
  UnsubscribedListingData
} from './types';

const subscribedFilter = {
  subscribed: true
};

const unSubscribedFilter = {
  subscribed: false
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: SubscriptionListingViews;
  value: SubscriptionListingViews;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const formatUnsubscribedData = (data: User[]): UnsubscribedListingData[] => {
  return data.map((employee) => {
    employee.subscription = employee.subscription as Subscription;

    return {
      id: employee.id,
      name: employee.name,
      email: employee.email,
      department_name: employee.department.name
    };
  });
};

const formatSubscribedData = (data: User[]): SubscribedListingData[] => {
  return data.map((employee) => {
    employee.subscription = employee.subscription as Subscription;

    return {
      id: employee.id,
      name: employee.name,
      email: employee.email,
      subscription_start_date: format(
        new Date(employee.subscription.start_date),
        'dd/MM/yyyy'
      ),
      subscription_end_date: format(
        new Date(employee.subscription.next_billing_date),
        'dd/MM/yyyy'
      ),
      subscription_plan: mapSubscriptionDurationToMonth(
        employee.subscription.duration
      ),
      subscription_state: employee.subscription.state
    };
  });
};

export function SubscriptionContainer() {
  const api = useApi();
  const config = useConfiguration();
  const navigate = useNavigate();
  const currency = config.currencyCode;
  const isFoodoraPro = isFoodora();
  const [, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { pushNotification, dismissNotification } = useNotification();
  const [action, setAction] = useAction<SubscriptionActions>();

  const [hasNoSubscription, setHasNoSubscription] = useState(false);
  const [listingView, setListingView] = useState<SubscriptionListingViews>(
    SubscriptionListingViews.Subscribed
  );
  const [subscribedEmployees, setSubscribedEmployees] = useState<
    SubscribedListingData[]
  >([]);
  const [unSubscribedEmployees, setUnsubscribedEmployees] = useState<
    UnsubscribedListingData[]
  >([]);
  const [employeeToSubscribe, setEmployeeToSubscribe] =
    useState<UnsubscribedListingData>();
  const [employeeToUnsubcribe, setEmployeeToUnsubscribe] =
    useState<SubscribedListingData>();
  const [departments, setDepartments] = useState<Department[]>([]);

  // for subscription page
  const [planCode, setPlanCode] = useState<string>('');
  const [selectionType, setSelectionType] = useState<SelectionType>(
    SelectionType.Department
  );
  const [selectedTierIndex, setSelectedTierIndex] = useState(0);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [departmentsToSubscribe, setDepartmentsToSubcribe] = useState<number[]>(
    []
  );

  const [employeeCountToSubscribe, setEmployeeCountToSubscribe] = useState(0);
  const [amountBilled, setAmountBilled] = useState(0);
  const [userCount, setUserCount] = useState(0);

  const openSubscribeDialog = () => {
    setEmployeeToSubscribe(undefined);
    setEmployeeToUnsubscribe(undefined);
    setEmployeeCountToSubscribe(0);
    setAmountBilled(0);
    setUserCount(0);

    setSelectionType(SelectionType.Department);
    setSelectedTierIndex(0);
    setDepartmentsToSubcribe([]);

    setAction(SubscriptionActions.Subscribe);
  };

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.SUBSCRIPTION });
  }, []);

  useEffect(() => {
    if (!tiers.length) {
      // reset on unselect department
      setEmployeeCountToSubscribe(0);
      setAmountBilled(0);

      return;
    }

    let newSubscriptions = 0;

    if (selectionType === SelectionType.Employee && employeeToSubscribe) {
      newSubscriptions = 1;
      setEmployeeCountToSubscribe(newSubscriptions);
      setAmountBilled(
        parseFloat(
          (newSubscriptions * tiers[selectedTierIndex].billed_price).toFixed(2)
        )
      );
    } else {
      if (departmentsToSubscribe.length == 0) {
        setEmployeeCountToSubscribe(0);
        setAmountBilled(0);
      } else {
        newSubscriptions = userCount;
        setEmployeeCountToSubscribe(userCount);
        setAmountBilled(
          parseFloat(
            (newSubscriptions * tiers[selectedTierIndex].billed_price).toFixed(
              2
            )
          )
        );
      }
    }
  }, [
    employeeToSubscribe,
    selectionType,
    selectedTierIndex,
    departmentsToSubscribe,
    tiers,
    departments,
    userCount
  ]);

  // params
  const queryParams = useQueryParam();

  const filters = ['search', 'count', 'offset', 'departments'];

  const filterParams = Object.keys(queryParams)
    .filter((key) => filters.includes(key))
    .reduce((params: Record<string, string>, key: string) => {
      params[key] = queryParams[key];
      return params;
    }, {});

  // queries
  useQuery(['plans'], () => api.company.subscription.plans(), {
    onSuccess: (data) => {
      const plan = data.data[0]; // only one plan for now
      setPlanCode(plan.code);
      setTiers(plan.tiers);
    }
  });

  useQuery(
    ['Users', { departmentsToSubscribe, subscribed: false, state: 'active' }],
    async () =>
      api.company.user.list({
        departments: departmentsToSubscribe.toString(),
        subscribed: false,
        state: 'active'
      }),
    {
      onSuccess: (data) => setUserCount(data.total),
      enabled: departmentsToSubscribe.length != 0
    }
  );

  const getSubscribedEmployees = useQuery(
    SubscriptionQueryKeys.list({ ...subscribedFilter, ...filterParams }),
    () => api.company.user.list({ ...subscribedFilter, ...filterParams })
  );

  const getUnsubscribedEmployees = useQuery(
    SubscriptionQueryKeys.list({ ...unSubscribedFilter, ...filterParams }),
    () => api.company.user.list({ ...unSubscribedFilter, ...filterParams })
  );

  useQuery(['departments'], () => api.company.department.list(), {
    onSuccess: (data) => setDepartments(data.data)
  });

  useEffect(() => {
    if (
      Object.keys(filterParams).length === 0 &&
      (getSubscribedEmployees.data?.total || 0) === 0
    ) {
      setHasNoSubscription(true);
    } else {
      setHasNoSubscription(false);
    }
  }, [filterParams, getSubscribedEmployees.data?.total]);

  useMemo(() => {
    setSubscribedEmployees(
      formatSubscribedData(getSubscribedEmployees.data?.data || [])
    );
  }, [getSubscribedEmployees.data?.data]);

  useMemo(
    () =>
      setUnsubscribedEmployees(
        formatUnsubscribedData(getUnsubscribedEmployees.data?.data || [])
      ),
    [getUnsubscribedEmployees.data?.data]
  );

  // mutations

  const subscribeMutation = useMutation<
    BulkSubscribeResponse,
    Error,
    BulkSubscribePayload
  >((payload) => api.company.subscription.bulkSubscribe(payload));

  const unSubscribeMutation = useMutation<
    BulkUnsubscribeResponse,
    Error,
    BulkUnsubscribePayload
  >((payload) => api.company.subscription.bulkUnsubscribe(payload));

  // handlers

  const handleSubscribe = () => {
    let payload: BulkSubscribePayload;
    const selectedTier = tiers[selectedTierIndex];
    const basePayload = {
      plan_code: planCode,
      tier_duration: selectedTier.duration_months
    };

    if (selectionType === SelectionType.Department) {
      if (departmentsToSubscribe.length === 0) {
        pushNotification(`Select departments to subscribe`);
        return;
      }
      payload = { ...basePayload, departments: departmentsToSubscribe };
    } else {
      if (employeeToSubscribe === undefined) {
        pushNotification(`Select employee to subscribe`);
        return;
      }
      payload = { ...basePayload, users: [employeeToSubscribe.id] };
    }

    const subscriptionInProgress = pushNotification('Subscribing...');

    subscribeMutation.mutate(payload, {
      onSuccess: (data) => {
        dismissNotification(subscriptionInProgress);
        pushNotification(`Subscribed ${data.data.total} employee(s)`, {
          variant: 'success'
        });

        queryClient.invalidateQueries(
          SubscriptionQueryKeys.list({ ...subscribedFilter, ...filterParams })
        );
        queryClient.invalidateQueries(
          SubscriptionQueryKeys.list({ ...unSubscribedFilter, ...filterParams })
        );
        setAction(false);
        setListingView(SubscriptionListingViews.Subscribed);
      },
      onError: (e) => {
        dismissNotification(subscriptionInProgress);
        pushNotification(e.message, {
          variant: 'error',
          onClick: handleSubscribe
        });
      }
    });
  };

  const handleUnsubscribe = () => {
    if (employeeToUnsubcribe === undefined) {
      pushNotification('No employee selected to unsubscribe');
      return;
    }

    const payload: BulkUnsubscribePayload = {
      users: [employeeToUnsubcribe.id]
    };

    const unSubscriptionInProgress = pushNotification('Unsubscribing...');
    unSubscribeMutation.mutate(payload, {
      onSuccess: (data) => {
        dismissNotification(unSubscriptionInProgress);
        pushNotification(`${data.data.total} employees unsubscribed`);

        queryClient.invalidateQueries(
          SubscriptionQueryKeys.list({ ...subscribedFilter, ...filterParams })
        );
        queryClient.refetchQueries(
          SubscriptionQueryKeys.list({
            ...unSubscribedFilter,
            ...filterParams
          }),
          {
            active: true
          }
        );
      },
      onError: (e) => {
        dismissNotification(unSubscriptionInProgress);
        pushNotification(e.message, {
          variant: 'error',
          onClick: handleUnsubscribe
        });
      }
    });
  };

  // Params for table

  const pageNumber = queryParams.offset
    ? parseInt(queryParams.offset)
    : undefined;

  const rowsPerPage = queryParams.count
    ? parseInt(queryParams.count)
    : undefined;

  const tableProps = {
    page: pageNumber
      ? Math.floor(pageNumber / (rowsPerPage || RowsPerPage.TWENTYFIVE))
      : undefined, // default is 0
    rowsPerPage: rowsPerPage // default is RowsPerPage.TWENTYFIVE (=25)
  };

  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...filterParams,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.Subscription,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    handlePageChange({
      offset: page ? page * rowsPerPage : undefined,
      count: rowsPerPage
    });
  };

  // 1. No subscribed employees
  // 2. subscribe button click should show subscribe page
  // 3. add employee click on subscribe page should show 'not subscribed' listing
  const showLandingPage =
    hasNoSubscription &&
    !action.includes(SubscriptionActions.Subscribe) &&
    listingView === SubscriptionListingViews.Subscribed;

  // need susbcribed employee count to decide to show landing page or listing
  if (getSubscribedEmployees.isLoading || getUnsubscribedEmployees.isLoading) {
    return <LoadingAnimation />;
  } else if (
    !getSubscribedEmployees.isSuccess ||
    !getUnsubscribedEmployees.isSuccess
  ) {
    return <>Error</>; //todo: check on error fallback
  }

  return showLandingPage ? (
    <LandingPage
      config={config}
      tiers={tiers}
      onSelectTier={(idx: number) => {
        setSelectedTierIndex(idx);
        setAction(SubscriptionActions.Subscribe);
      }}
    />
  ) : (
    <Page
      pageOptions={{ fullDialogWidth: 850 }}
      actions={{
        [SubscriptionActions.Subscribe]: {
          component: (
            <SubscribePage
              config={config}
              selectionType={selectionType}
              setSelectionType={setSelectionType}
              departments={departments}
              tiers={tiers}
              selectedDepartments={departmentsToSubscribe}
              selectedEmployees={
                employeeToSubscribe ? [employeeToSubscribe] : []
              }
              selectedTierIndex={selectedTierIndex}
              employeeCountToSubscribe={employeeCountToSubscribe}
              amountBilled={amountBilled}
              setSelectedDepartments={setDepartmentsToSubcribe}
              setSelectedTierIndex={setSelectedTierIndex}
              onSelectEmployeesClick={() => {
                setAction(false);
                setListingView(SubscriptionListingViews.Unsubscribed);
              }}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: isFoodoraPro
              ? LabelText.subscribePageComponent.altTitle
              : LabelText.subscribePageComponent.title,
            action: {
              label: LabelText.subscribePageComponent.button,
              callback: () => {
                setAction(SubscriptionActions.SubscribeConfirm);
              }
            }
          }
        },
        [SubscriptionActions.Unsubscribe]: {
          component: (
            <>
              <Button variant="secondary">
                {LabelText.unsubscribeModal.cancelButton}
              </Button>
              <Button variant="primary" onClick={handleUnsubscribe}>
                {LabelText.unsubscribeModal.confirmButton}
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: `Unsubscribe ${employeeToUnsubcribe?.name} from ${
              isFoodoraPro ? 'foodora PRO' : 'pandapro'
            }`,
            content: `${employeeToUnsubcribe?.name} will continue enjoying ${
              isFoodoraPro ? 'foodora PRO' : 'pandapro'
            } benefits until their next renewal date on ${
              employeeToUnsubcribe?.subscription_end_date
            }`
          }
        },
        [SubscriptionActions.SubscribeConfirm]: {
          component: (
            <>
              <Button variant="secondary">
                {LabelText.SUBSCRIBE_CONFIRM_MODAL.CANCEL_BUTTON}
              </Button>
              <Button variant="primary" onClick={handleSubscribe}>
                {LabelText.SUBSCRIBE_CONFIRM_MODAL.CONFIRM_BUTTON}
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: LabelText.SUBSCRIBE_CONFIRM_MODAL.TITLE,
            content: `You are subscribing ${employeeCountToSubscribe} employee(s) to ${
              isFoodoraPro ? 'foodora PRO' : 'pandapro'
            }. ${currency} ${amountBilled} will be billed to your next invoice`
          }
        }
      }}
    >
      <PageHeader
        title={isFoodoraPro ? LabelText.altTitle : LabelText.title}
        subtitle={<>{LabelText.subtitle}</>}
        buttons={[
          <Button key="1" onClick={() => openSubscribeDialog()}>
            {LabelText.SUBSCRIBE_BUTTON}
          </Button>
        ]}
      />
      <Tabs
        value={listingView}
        onChange={(_, value) => setListingView(value)}
        indicatorColor="primary"
        sx={{
          '.MuiButtonBase-root': {
            textTransform: 'none',
            '&.Mui-selected': {
              color: 'interaction.primary'
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: 'interaction.primary'
          }
        }}
      >
        <Tab
          disableRipple
          label={
            LabelText.tab.subscribed +
            ` (${getSubscribedEmployees.data?.total})`
          }
          value={SubscriptionListingViews.Subscribed}
          disabled={hasNoSubscription}
        />
        <Tab
          disableRipple
          label={
            LabelText.tab.unsubscribed +
            ` (${getUnsubscribedEmployees.data?.total})`
          }
          value={SubscriptionListingViews.Unsubscribed}
        />
      </Tabs>
      <TabPanel value={listingView} index={SubscriptionListingViews.Subscribed}>
        <TableList<SubscribedListingData>
          loading={getSubscribedEmployees.isLoading}
          columns={SubscribedColumns}
          data={subscribedEmployees}
          total={getSubscribedEmployees.data?.total}
          onPaginationChange={handlePaginationChange}
          empty={
            <CallToActions
              illustration={(props: React.SVGProps<SVGSVGElement>) => (
                <ReferAFriend {...props} />
              )}
              title={LabelText.SUBSCRIBED_LISTING.CTA_TITLE}
              subtitle={LabelText.SUBSCRIBED_LISTING.CTA_SUBTITLE}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
              >
                {LabelText.SUBSCRIBED_LISTING.CTA_BUTTON}
              </Button>
            </CallToActions>
          }
          action={(employee) => (
            <ButtonCircular
              aria-label="menu"
              size="small"
              onClick={() => {
                setEmployeeToUnsubscribe(employee);
                setAction(SubscriptionActions.Unsubscribe);
              }}
            >
              <IcUnsubscribe data-testid={TestId.icon.unsubscribe} />
            </ButtonCircular>
          )}
          {...tableProps}
        >
          <SubscribedListingFilter
            onPageChange={handlePageChange}
            params={filterParams}
          />
        </TableList>
      </TabPanel>
      <TabPanel
        value={listingView}
        index={SubscriptionListingViews.Unsubscribed}
      >
        <TableList<UnsubscribedListingData>
          columns={UnsubscribedColumns}
          total={getUnsubscribedEmployees.data?.total}
          empty={
            <CallToActions
              illustration={(props: React.SVGProps<SVGSVGElement>) => (
                <AllSubscribed {...props} />
              )}
              title={LabelText.UNSUBSCRIBED_LISTING.CTA_TITLE}
              subtitle={LabelText.UNSUBSCRIBED_LISTING.CTA_SUBTITLE}
            ></CallToActions>
          }
          data={unSubscribedEmployees}
          onPaginationChange={handlePaginationChange}
          action={(employee) => (
            <ButtonCircular
              aria-label="menu"
              size="small"
              onClick={() => {
                setEmployeeToSubscribe(employee);
                setSelectionType(SelectionType.Employee);
                setAction(SubscriptionActions.Subscribe);
              }}
            >
              <IcSubscribe data-testid={TestId.icon.subscribe} />
            </ButtonCircular>
          )}
          {...tableProps}
        >
          <UnsubscribedListingFilter
            onPageChange={handlePageChange}
            params={filterParams}
            departments={departments}
          />
        </TableList>
      </TabPanel>
    </Page>
  );
}
